import { queryRiskLevel, queryRiskLevelByCampaigns } from '@/api/google/adManagement';
import { promoteInfo } from '@/api/google/create';
import { regUrl } from '@/views/google/create/components/campaignInfo/index';
import {
  confirmOptions,
  parseSaleId,
  riskFacts,
  riskSentence,
  singleRiskMessage,
} from '@/views/adManagement/components/editCopy/riskLevelCheck';
import '@/views/adManagement/components/editCopy/riskLevelConfirm.css';

function text(value) {
  return value == null ? '' : String(value);
}

export function googleAccountId(account) {
  const fromAccount = text(account && (account.accountId || account.id)).trim();
  if (fromAccount) return fromAccount;
  try {
    return text(localStorage.getItem('googleAccountId')).trim();
  } catch (e) {
    return '';
  }
}

export function googleAccountName(account) {
  return text(account && (account.accountName || account.name)).trim();
}

const fillWarning = '风控等级暂时无法确认，可继续填写';
const continueWarning = '风控等级暂时无法确认，可继续';
const partialWarning = '部分系列风控等级暂时无法确认，可继续';

function warn(vm, message) {
  vm.$message.warning(message);
}

/**
 * 创建页商品已经写进表单之后核对。点取消执行 onCancel。
 */
export function checkGoogleSaleRisk(vm, options) {
  const saleId = parseSaleId(options && options.saleId);
  const accountId = text(options && options.accountId).trim();
  const accountName = text(options && options.accountName);
  if (!vm || !accountId || saleId == null) return;
  const key = accountId + '\0' + saleId;
  if (vm.riskCheckedKey === key) return;
  vm.riskCheckedKey = key;
  const seq = ++vm.riskCheckSeq;
  const stillCurrent = options.stillCurrent || (() => true);
  queryRiskLevel({ accountId, saleId })
    .then((res) => {
      if (seq !== vm.riskCheckSeq) return;
      if (!res || res.code != 0) {
        if (vm.riskCheckedKey === key) vm.riskCheckedKey = '';
        warn(vm, fillWarning);
        return;
      }
      if (!res.data || res.data.hasRisk !== true) return;
      if (!stillCurrent(saleId, accountId)) return;
      const name = text(res.data.accountId) === accountId ? accountName : '';
      vm.$nextTick(() => {
        vm.$confirm(singleRiskMessage(res.data, name), '风险提示', confirmOptions)
          .then(() => {})
          .catch(() => {
            if (seq !== vm.riskCheckSeq) return;
            if (vm.riskCheckedKey === key) vm.riskCheckedKey = '';
            if (options.onCancel) options.onCancel();
          });
      });
    })
    .catch(() => {
      if (seq !== vm.riskCheckSeq) return;
      if (vm.riskCheckedKey === key) vm.riskCheckedKey = '';
      warn(vm, fillWarning);
    });
}

function uniqueCampaignIds(rows) {
  const ids = [];
  const seen = new Set();
  (rows || []).forEach((row) => {
    const id = text(row && row.campaign_id).trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

function rowAccountId(row) {
  return text(row && (row.ad_accountId || row.rowAccountId)).trim();
}

function groupAccountName(rows, returnedAccountId, requestedAccountId) {
  if (text(returnedAccountId) !== text(requestedAccountId)) return '';
  const row = (rows || []).find((item) => item && item.ad_accountName);
  return text(row && row.ad_accountName);
}

/**
 * 打开复制抽屉之前，以及系列开启之前。系列 ID 用 campaign_id。返回 true 才继续。
 */
export async function confirmGoogleCampaignRisk(vm, rows) {
  const level = vm.level;
  const groups = new Map();
  (rows || []).forEach((row) => {
    const accountId = rowAccountId(row);
    if (!accountId) return;
    if (!groups.has(accountId)) groups.set(accountId, []);
    groups.get(accountId).push(row);
  });

  const risks = [];
  let requested = 0;
  let requestFailed = 0;
  let hasUnconfirmed = false;
  for (const [accountId, groupRows] of groups) {
    const campaignIds = uniqueCampaignIds(groupRows);
    if (!campaignIds.length) continue;
    requested += 1;
    const names = new Map();
    if (level === 'campaign') {
      groupRows.forEach((row) => {
        const id = text(row && row.campaign_id).trim();
        if (id && row.name) names.set(id, row.name);
      });
    }
    try {
      const res = await queryRiskLevelByCampaigns({ accountId, campaignIds });
      if (!res || res.code != 0 || !res.data) {
        requestFailed += 1;
        continue;
      }
      (res.data.risks || []).forEach((item) => {
        if (item.hasRisk !== true) return;
        const rowName = names.get(String(item.campaignId));
        risks.push({
          html: riskFacts({
            campaignId: item.campaignId,
            campaignName: rowName || item.campaignName,
            productId: item.productId,
            productName: item.productName,
            productRiskLevel: item.productRiskLevel,
            accountId: item.accountId,
            accountName: groupAccountName(groupRows, item.accountId, accountId),
            accountRiskLevel: item.accountRiskLevel,
          }),
          productLevel: text(item.productRiskLevel),
          accountLevel: text(item.accountRiskLevel),
        });
      });
      if ((res.data.unconfirmed || []).length) hasUnconfirmed = true;
    } catch (e) {
      requestFailed += 1;
    }
  }

  if (!requested) return true;
  if (!risks.length) {
    if (requestFailed === requested) {
      warn(vm, continueWarning);
    } else if (requestFailed > 0 || hasUnconfirmed) {
      warn(vm, partialWarning);
    }
    return true;
  }

  const pairs = new Set(risks.map((item) => item.productLevel + '\0' + item.accountLevel));
  const sentence = pairs.size === 1 ? riskSentence(risks[0].productLevel, risks[0].accountLevel) : riskSentence('', '');
  const extra = requestFailed > 0 || hasUnconfirmed ? '<span class="risk-level-extra">部分系列风控等级暂时无法确认，可继续</span>' : '';
  try {
    await vm.$confirm(risks.map((item) => item.html).join('') + sentence + extra, '风险提示', confirmOptions);
    return true;
  } catch (e) {
    return false;
  }
}

function copyAccountName(vm, returnedAccountId, requestedAccountId) {
  if (text(returnedAccountId) !== text(requestedAccountId)) return '';
  const found = (vm.accountList || []).find((item) => text(item && item.accountId) === text(requestedAccountId));
  return text(found && (found.accountName || found.name));
}

/**
 * 系列复制抽屉里「使用指定商品」解析出商品后，按目标账户再查一次。
 */
export function checkGoogleCopyAssignRisk(vm) {
  if (!vm || !vm.ruleForm || vm.ruleForm.copySaleType !== '使用指定商品') return;
  const link = text(vm.ruleForm.promotionLink).trim();
  const accountId = text(vm.ruleForm.toAccountId).trim();
  if (!link || !accountId || !regUrl.test(link)) return;
  const seq = ++vm.riskCheckSeq;
  const utmSourceConfig = vm.getUtmSourceConfig ? vm.getUtmSourceConfig() : null;
  promoteInfo({ promotionLink: link, utmSourceConfig })
    .then((res) => {
      if (seq !== vm.riskCheckSeq) return;
      if (!vm.ruleForm || vm.ruleForm.copySaleType !== '使用指定商品') return;
      if (text(vm.ruleForm.promotionLink).trim() !== link || text(vm.ruleForm.toAccountId).trim() !== accountId) return;
      if (!res || res.code != 0) {
        warn(vm, fillWarning);
        return;
      }
      const saleId = parseSaleId(res.data && res.data.sale && res.data.sale.saleId);
      if (saleId == null) return;
      const key = accountId + '\0' + saleId;
      if (vm.riskCheckedKey === key) return;
      vm.riskCheckedKey = key;
      return queryRiskLevel({ accountId, saleId })
        .then((riskRes) => {
          if (seq !== vm.riskCheckSeq) return;
          if (!riskRes || riskRes.code != 0) {
            if (vm.riskCheckedKey === key) vm.riskCheckedKey = '';
            warn(vm, fillWarning);
            return;
          }
          if (!riskRes.data || riskRes.data.hasRisk !== true) return;
          if (vm.ruleForm.copySaleType !== '使用指定商品') return;
          const currentSaleStill = text(vm.ruleForm.promotionLink).trim() === link && text(vm.ruleForm.toAccountId).trim() === accountId;
          if (!currentSaleStill) return;
          const name = copyAccountName(vm, riskRes.data.accountId, accountId);
          vm.$nextTick(() => {
            vm.$confirm(singleRiskMessage(riskRes.data, name), '风险提示', confirmOptions)
              .then(() => {})
              .catch(() => {
                if (seq !== vm.riskCheckSeq) return;
                if (vm.riskCheckedKey === key) vm.riskCheckedKey = '';
                vm.$set(vm.ruleForm, 'promotionLink', '');
              });
          });
        })
        .catch(() => {
          if (seq !== vm.riskCheckSeq) return;
          if (vm.riskCheckedKey === key) vm.riskCheckedKey = '';
          warn(vm, fillWarning);
        });
    })
    .catch(() => {
      if (seq !== vm.riskCheckSeq) return;
      warn(vm, fillWarning);
    });
}
